import mitt from 'mitt';
import { BloomreachEventNames } from './constants';
/** Max buffered events before the SDK loads. Prevents unbounded memory growth. */
const MAX_QUEUE_SIZE = 100;
const emitter = mitt();
/** Events tracked before the SDK is ready. Flushed when SDK becomes available. */
const eventQueue = [];
const state = {
    initialized: false,
    enabled: false,
    started: false,
    debug: false,
};
const log = (...args) => {
    if (state.debug) {
        console.debug('[Bloomreach]', ...args);
    }
};
const getBrweb = () => {
    var _a;
    if (typeof window === 'undefined') {
        return undefined;
    }
    return ((_a = window.brweb) === null || _a === void 0 ? void 0 : _a.track) ? window.brweb : undefined;
};
/**
 * Ensures brweb.start() has been called. This handles the race condition where
 * React hydrates before brweb.min.js finishes loading. Called both at init
 * time and on every event dispatch so start() happens as soon as the SDK is ready.
 */
const ensureStarted = () => {
    const sdk = getBrweb();
    if (!sdk) {
        return undefined;
    }
    if (!state.started && !sdk.init) {
        log('Calling brweb.start()');
        sdk.start();
        state.started = true;
    }
    return sdk;
};
/**
 * Maps internal event names to dedicated SDK methods.
 * Events not in this map are dispatched via the generic sdk.track().
 */
const SDK_METHOD_MAP = {
    [BloomreachEventNames.IDENTIFY]: (sdk, properties) => sdk.identify((properties !== null && properties !== void 0 ? properties : {})),
    [BloomreachEventNames.ANONYMIZE]: sdk => sdk.anonymize(),
    [BloomreachEventNames.UPDATE]: (sdk, properties) => sdk.update(properties !== null && properties !== void 0 ? properties : {}),
};
const dispatchToSdk = (sdk, { type, properties }) => {
    log(type, properties);
    const method = SDK_METHOD_MAP[type];
    method ? method(sdk, properties) : sdk.track(type, properties);
};
const flushQueue = (sdk) => {
    if (eventQueue.length === 0) {
        return;
    }
    log('Flushing', eventQueue.length, 'queued events');
    while (eventQueue.length > 0) {
        dispatchToSdk(sdk, eventQueue.shift());
    }
};
const processEvent = (event) => {
    const sdk = ensureStarted();
    if (!sdk) {
        if (eventQueue.length < MAX_QUEUE_SIZE) {
            eventQueue.push(event);
        }
        return;
    }
    flushQueue(sdk);
    dispatchToSdk(sdk, event);
};
export const initBloomreach = (config, debug = false) => {
    if (state.initialized) {
        return;
    }
    state.initialized = true;
    if (!config.isEnabled) {
        return;
    }
    state.enabled = true;
    state.debug = debug;
    ensureStarted();
    log('Initialized');
    emitter.on('*', (_type, event) => {
        try {
            processEvent(event);
        }
        catch (e) {
            console.error('[Bloomreach] tracking error', e);
        }
    });
};
export const trackBloomreach = (eventType, properties) => {
    if (!state.enabled) {
        return;
    }
    emitter.emit(eventType, { type: eventType, properties });
};
export const identifyBloomreach = (customerIds) => {
    trackBloomreach(BloomreachEventNames.IDENTIFY, customerIds);
};
export const anonymizeBloomreach = () => {
    trackBloomreach(BloomreachEventNames.ANONYMIZE);
};
export const onBloomreachEvent = (handler) => {
    const wildcardHandler = (_type, event) => handler(event);
    emitter.on('*', wildcardHandler);
    return () => emitter.off('*', wildcardHandler);
};
/** Reset all state. Only for testing — do not use in production code. */
export const resetBloomreach = () => {
    state.initialized = false;
    state.enabled = false;
    state.started = false;
    state.debug = false;
    eventQueue.length = 0;
    emitter.all.clear();
};
